import React, { useEffect, useMemo, useState } from 'react';
import {
	Dialog,
	DialogContent,
	DialogActions,
	Button,
	Grid,
	Typography,
	Box,
	CircularProgress,
	Switch,
	FormControlLabel,
} from '@mui/material';
import { useQuery } from 'react-query';
import dayjs from 'dayjs';
import { useWorkplacesContext } from 'module/workplaces/context/WorkplacesContext';
import {
	fetchSingleEvent,
	fetchAllEventTypes,
	fetchAllLocations,
	getEventDocumentByReference,
} from 'core/API/events';
import { getJobCodes } from 'core/API/scheduler';
import { fetchAllCustomers } from 'core/API/customers';
import { ReadonlyFormField } from 'core/components/ReadonlyFormField';
import styles from './ScheduleEventDetailDialog.module.scss';

interface ScheduleEventDetailDialogProps {
	open: boolean;
	event: any;
	eventTypeLabel?: string;
	timezone?: string;
	container?: Element | (() => Element | null) | null;
	onClose: () => void;
}

const resolveEventId = (value: any): number | null => {
	if (value === null || value === undefined) return null;
	if (typeof value === 'number') return Number.isFinite(value) ? value : null;
	if (typeof value === 'string') {
		const match = value.match(/(\d+)(?:\/)?$/);
		if (!match) return null;
		const parsed = Number(match[1]);
		return Number.isFinite(parsed) ? parsed : null;
	}
	if (typeof value === 'object') {
		if (value.id !== undefined && value.id !== null) return resolveEventId(value.id);
		if (value['@id']) return resolveEventId(value['@id']);
	}
	return null;
};

const extractIdFromIri = (iri: any): number => {
	if (!iri) return 0;
	if (typeof iri === 'number') return Number.isFinite(iri) ? iri : 0;
	if (typeof iri === 'string') {
		const id = parseInt(iri.split('/').pop() || '0', 10);
		return Number.isNaN(id) ? 0 : id;
	}
	if (typeof iri === 'object') {
		if (iri.id !== undefined && iri.id !== null) return extractIdFromIri(iri.id);
		if (iri['@id']) return extractIdFromIri(iri['@id']);
	}
	return 0;
};

const formatDateForDisplay = (dateStr: string): string => {
	if (!dateStr) return '';
	const match = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})/);
	if (match) return `${match[2]}/${match[3]}/${match[1]}`;
	return dateStr;
};

const formatTimeForDisplay = (timeStr: string): string => {
	if (!timeStr) return '';
	const d = dayjs()
		.startOf('day')
		.add(parseInt(String(timeStr).split(':')[0] || '0', 10), 'hour')
		.add(parseInt(String(timeStr).split(':')[1] || '0', 10), 'minute');
	return d.format('h:mm A');
};

const toDocumentRef = (item: any): string => {
	if (!item) return '';
	if (typeof item === 'string') return item.trim();
	if (typeof item === 'object') {
		return String(item['@id'] || item.filePath || item.file_path || item.path || '').trim();
	}
	return '';
};

const normalizeDocumentsToList = (raw: any): string[] => {
	if (!raw) return [];
	if (Array.isArray(raw)) return raw.map((x) => toDocumentRef(x)).filter(Boolean);
	if (typeof raw === 'string') {
		return raw.split(',').map((x) => x.trim()).filter(Boolean);
	}
	return [];
};

const ScheduleEventDetailDialog: React.FC<ScheduleEventDetailDialogProps> = ({
	open,
	event,
	eventTypeLabel,
	container,
	onClose,
}) => {
	const { workplaceId } = useWorkplacesContext();
	const [documentLinks, setDocumentLinks] = useState<Array<{ ref: string; url: string; label: string }>>([]);
	const [documentsLoading, setDocumentsLoading] = useState(false);

	const eventId =
		resolveEventId(event?.id) ||
		resolveEventId(event?.['@id']) ||
		null;

	const { data: fetchedEvent, isLoading: isLoadingEvent } = useQuery(
		['schedule-event-detail', eventId],
		() => fetchSingleEvent(eventId!),
		{
			enabled: open && !!eventId,
			initialData: event,
			staleTime: 0,
			refetchOnMount: 'always',
		}
	);

	const eventData = fetchedEvent || event;

	const eventTypeId = eventData?.type ? extractIdFromIri(eventData.type) : 0;
	const locationId = eventData?.location ? extractIdFromIri(eventData.location) : 0;
	const customerId = eventData?.customer ? extractIdFromIri(eventData.customer) : 0;

	const { data: eventTypes } = useQuery(
		['eventTypes', workplaceId],
		() => fetchAllEventTypes(workplaceId),
		{ enabled: open && !!workplaceId, staleTime: Infinity }
	);

	const { data: customers } = useQuery(['customers'], () => fetchAllCustomers(), {
		enabled: open,
		staleTime: 0,
		refetchOnMount: 'always',
	});

	const { data: jobCodesData } = useQuery(['jobCodes'], () => getJobCodes(), {
		enabled: open,
		staleTime: Infinity,
	});

	const { data: locations } = useQuery(
		['locations', workplaceId],
		() => fetchAllLocations(workplaceId),
		{ enabled: open && !!workplaceId, staleTime: Infinity }
	);

	const sortedJobCodesData = useMemo(() => {
		if (!jobCodesData) return [];
		return [...jobCodesData].sort((a, b) => (a.jobNumber ?? 999) - (b.jobNumber ?? 999));
	}, [jobCodesData]);

	const selectedEventType = useMemo(() => {
		if (!eventTypes?.member && !eventTypeId) {
			return eventTypeLabel ? { label: eventTypeLabel } : null;
		}
		if (!eventTypes?.member || !eventTypeId) return eventTypeLabel ? { label: eventTypeLabel } : null;
		return eventTypes.member.find((et: any) => et.id === eventTypeId) || null;
	}, [eventTypes, eventTypeId, eventTypeLabel]);

	const selectedLocation = useMemo(() => {
		if (!locations?.member || !locationId) return null;
		return locations.member.find((loc: any) => loc.id === locationId) || null;
	}, [locations, locationId]);

	const selectedCustomer = useMemo(() => {
		if (customerId && customers?.member) {
			const found = customers.member.find((c: any) => c.id === customerId);
			if (found) return found;
		}
		if (eventData?.customer && typeof eventData.customer === 'object') {
			return eventData.customer;
		}
		return null;
	}, [customerId, customers?.member, eventData?.customer]);

	const hasCustomer = !!(selectedCustomer || customerId);

	const customerName =
		selectedCustomer?.fullName ||
		[selectedCustomer?.firstName, selectedCustomer?.lastName].filter(Boolean).join(' ') ||
		'';

	const eventStaffing: any[] =
		(eventData as any)?.eventStaffing || (eventData as any)?.event_staffing || [];

	useEffect(() => {
		if (!open || !eventData) return;

		let cancelled = false;
		const uploadsBase = (process.env.REACT_APP_IMAGE_URL || '').replace(/\/$/, '');
		const refs = normalizeDocumentsToList(
			(eventData as any)?.documents || (eventData as any)?.banquet_event_order
		);

		const resolveOne = async (ref: string) => {
			if (/^https?:\/\//i.test(ref)) {
				return { ref, url: ref, label: ref.split('/').pop() || 'Open PDF' };
			}
			if (ref.startsWith('/api/')) {
				try {
					const doc = await getEventDocumentByReference(ref);
					const raw = doc.filePath || doc.file_path || doc.path || '';
					if (!raw) return { ref, url: '', label: 'Preview unavailable' };
					const clean = String(raw).replace(/^\/+/, '').replace(/^uploads\//, '');
					const url = `${uploadsBase}/${clean.startsWith('event_documents/') ? clean : `event_documents/${clean}`}`;
					const label = doc.fileName || doc.file_name || clean.split('/').pop() || 'Open PDF';
					return { ref, url, label };
				} catch {
					return { ref, url: '', label: 'Preview unavailable' };
				}
			}
			if (!ref.includes('/') && ref.toLowerCase().endsWith('.pdf')) {
				const clean = ref.replace(/^\/+/, '');
				return { ref, url: `${uploadsBase}/event_documents/${clean}`, label: clean };
			}
			const clean = ref.replace(/^\/+/, '').replace(/^uploads\//, '');
			return {
				ref,
				url: `${uploadsBase}/${clean.startsWith('event_documents/') ? clean : `event_documents/${clean}`}`,
				label: clean.split('/').pop() || 'Open PDF',
			};
		};

		const resolveAll = async () => {
			if (!refs.length) {
				if (!cancelled) setDocumentLinks([]);
				return;
			}
			setDocumentsLoading(true);
			const out = await Promise.all(refs.map((r) => resolveOne(r)));
			if (!cancelled) {
				setDocumentLinks(out);
				setDocumentsLoading(false);
			}
		};

		resolveAll();
		return () => {
			cancelled = true;
		};
	}, [open, eventData]);

	if (!event) return null;

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth="md"
			fullWidth
			container={container}
			PaperProps={{ className: styles.dialogPaper }}
		>
			<DialogContent className={styles.content}>
				<h2 className={styles.title}>View Event</h2>

				{isLoadingEvent && !eventData ? (
					<div className={styles.loadingWrap}>
						<CircularProgress />
					</div>
				) : (
					<Grid container spacing={2}>
						<Grid item xs={12} sm={6}>
							<ReadonlyFormField label="Event Type" value={selectedEventType?.label || ''} />
						</Grid>
						<Grid item xs={12} sm={6} />

						<Grid item xs={12}>
							<Box className={styles.sectionHeaderRow}>
								<Typography variant="subtitle1" className={styles.sectionTitle} sx={{ borderBottom: 'none', mb: 0 }}>
									Customer Details
								</Typography>
								<FormControlLabel
									control={
										<Switch
											checked={hasCustomer}
											disabled
											size="small"
											sx={{
												'& .MuiSwitch-switchBase.Mui-checked': { color: 'rgb(68, 99, 136)' },
												'& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
													backgroundColor: 'rgb(68, 99, 136)',
												},
												'& .MuiSwitch-switchBase.Mui-disabled.Mui-checked': {
													color: 'rgb(68, 99, 136)',
												},
												'& .MuiSwitch-switchBase.Mui-disabled.Mui-checked + .MuiSwitch-track': {
													backgroundColor: 'rgb(68, 99, 136)',
													opacity: 0.7,
												},
											}}
										/>
									}
									label={
										<Typography variant="body2" sx={{ fontFamily: 'Gotham', color: '#3c5471' }}>
											Include customer
										</Typography>
									}
									labelPlacement="start"
									sx={{ mr: 0 }}
								/>
							</Box>
						</Grid>

						{hasCustomer && (
							<>
								<Grid item xs={12} sm={6}>
									<ReadonlyFormField label="Customer Name" value={customerName} />
								</Grid>
								<Grid item xs={12} sm={6}>
									<ReadonlyFormField
										label="Customer Email"
										value={selectedCustomer?.email || (eventData as any)?.organizer_email || ''}
									/>
								</Grid>
								<Grid item xs={12} sm={6}>
									<ReadonlyFormField
										label="Customer Phone Number"
										value={selectedCustomer?.phone || (eventData as any)?.organizer_phone_number || ''}
									/>
								</Grid>
								<Grid item xs={12} sm={6}>
									<ReadonlyFormField
										label="Tax Exempt ID"
										value={
											eventData?.taxExemptId ||
											(eventData as any)?.tax_exempt_id ||
											selectedCustomer?.taxExemptId ||
											''
										}
									/>
								</Grid>
							</>
						)}

						<Grid item xs={12}>
							<Typography variant="subtitle1" className={styles.sectionTitle}>
								Event Details
							</Typography>
						</Grid>

						<Grid item xs={12} sm={6}>
							<ReadonlyFormField
								label="Event Name"
								value={eventData?.name || eventData?.title || ''}
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<ReadonlyFormField
								label="Location"
								value={selectedLocation?.label || selectedLocation?.name || ''}
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<ReadonlyFormField
								label="Start Date"
								value={formatDateForDisplay(eventData?.startDate || eventData?.start || '')}
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<ReadonlyFormField
								label="Start Time"
								value={formatTimeForDisplay(eventData?.startTime || '')}
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<ReadonlyFormField
								label="End Date"
								value={formatDateForDisplay(eventData?.endDate || eventData?.end || '')}
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<ReadonlyFormField
								label="End Time"
								value={formatTimeForDisplay(eventData?.endTime || '')}
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<ReadonlyFormField
								label="Number of Attendees"
								value={eventData?.numberOfAttendees || eventData?.numOfAtendees || 1}
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<ReadonlyFormField
								label="Tax Exempt ID"
								value={
									eventData?.taxExemptId ||
									(eventData as any)?.tax_exempt_id ||
									selectedCustomer?.taxExemptId ||
									''
								}
							/>
						</Grid>

						<Grid item xs={12}>
							<label className={styles.formLabel}>Job Roles to Support Event</label>
							{eventStaffing.length > 0 ? (
								eventStaffing.map((staff: any, index: number) => {
									const jobCodeId = staff.jobCode ? extractIdFromIri(staff.jobCode) : null;
									const jobCodeItem = sortedJobCodesData.find((jc: any) => jc.id === jobCodeId);

									return (
										<Box key={index} sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
											<Box sx={{ flex: 1 }}>
												<ReadonlyFormField
													label="Job Code"
													value={
														jobCodeItem
															? `${jobCodeItem.jobNumber} - ${jobCodeItem.label}`
															: ''
													}
												/>
											</Box>
											<Box sx={{ flex: 1 }}>
												<ReadonlyFormField
													label="Number Of Additional Staff"
													value={staff.numberOfAdditionalStaff || 0}
												/>
											</Box>
										</Box>
									);
								})
							) : (
								<div className={styles.emptyPlaceholder}>No job roles assigned</div>
							)}
						</Grid>

						<Grid item xs={12}>
							<label className={styles.formLabel}>Banquet Event Order / Documents</label>
							{documentsLoading ? (
								<CircularProgress size={20} />
							) : documentLinks.length > 0 ? (
								<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
									{documentLinks.map((doc, idx) => (
										<div key={`${doc.ref}-${idx}`} className={styles.documentLink}>
											{doc.url ? (
												<a href={doc.url} target="_blank" rel="noopener noreferrer">
													{doc.label}
												</a>
											) : (
												<Typography variant="body2" color="text.secondary">
													{doc.label}
												</Typography>
											)}
										</div>
									))}
								</Box>
							) : (
								<div className={styles.emptyPlaceholder}>No documents attached</div>
							)}
						</Grid>

						<Grid item xs={12}>
							<ReadonlyFormField
								label="Notes"
								value={eventData?.notes || eventData?.note || ''}
								multiline
							/>
						</Grid>
					</Grid>
				)}
			</DialogContent>

			<DialogActions className={styles.actions}>
				<Button className={styles.closeButton} onClick={onClose}>
					Close
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default ScheduleEventDetailDialog;
